// Platform event surface — the SDK emits structured events on every
// observable operation so the devtools overlay (and any other listener)
// can render a live log. Events are best-effort fire-and-forget;
// handlers errors don't propagate back to the SDK call.

/** Typed event payloads. Add new events here as they're instrumented. */
export interface PlatformEventMap {
  /** A `documents.append(...)` call completed (success or failure). */
  "documents.append": {
    prefix: string;
    key?: string;
    durationMs: number;
    ok: boolean;
    error?: string;
  };
  /** A `documents.list(...)` iteration completed. Fires once at end. */
  "documents.list": {
    prefix: string;
    count: number;
    durationMs: number;
    ok: boolean;
    error?: string;
  };
  /** A `documents.get(...)` call completed. */
  "documents.get": {
    prefix: string;
    key: string;
    durationMs: number;
    ok: boolean;
    error?: string;
  };
  /** A `documents.remove(...)` call completed. */
  "documents.remove": {
    prefix: string;
    key: string;
    durationMs: number;
    ok: boolean;
    error?: string;
  };
  /** STS credentials were (re-)minted via AssumeRoleWithWebIdentity. */
  "sts.refresh": {
    newExpiration: string;
    durationMs: number;
  };
  /** A new S3 client instance was constructed (initial or after creds rotation). */
  "s3.client.built": {
    reason: "first" | "creds-rotated";
  };
  /** The OIDC session can no longer mint AWS credentials and no silent
   *  refresh is possible — the user must sign in again. UIs listen for
   *  this to prompt re-login instead of surfacing the raw AWS error. */
  "session.expired": {
    reason: "refresh-failed" | "no-refresh-token" | "token-rejected";
  };
}

export type PlatformEventName = keyof PlatformEventMap;

export type PlatformEventHandler<K extends PlatformEventName> = (
  payload: PlatformEventMap[K],
) => void;

export type PlatformWildcardHandler = <K extends PlatformEventName>(
  name: K,
  payload: PlatformEventMap[K],
) => void;

export interface PlatformEvents {
  /** Subscribe to a specific event. Returns an unsubscribe function. */
  on<K extends PlatformEventName>(name: K, handler: PlatformEventHandler<K>): () => void;
  /** Subscribe to every event. Useful for devtools overlays. */
  on(name: "*", handler: PlatformWildcardHandler): () => void;
  /** Unsubscribe a previously-registered handler. */
  off<K extends PlatformEventName>(name: K, handler: PlatformEventHandler<K>): void;
  off(name: "*", handler: PlatformWildcardHandler): void;
  /** Emit an event. Handlers run synchronously; their errors are swallowed
   *  and logged, so a bad listener can't break the SDK call that fired. */
  emit<K extends PlatformEventName>(name: K, payload: PlatformEventMap[K]): void;
}

export function createEvents(): PlatformEvents {
  // Plain maps; no Set so insertion order is stable for predictable overlay rendering.
  const specific = new Map<PlatformEventName, Array<PlatformEventHandler<PlatformEventName>>>();
  const wildcards: PlatformWildcardHandler[] = [];

  function on<K extends PlatformEventName>(
    name: K | "*",
    handler: PlatformEventHandler<K> | PlatformWildcardHandler,
  ): () => void {
    if (name === "*") {
      wildcards.push(handler as PlatformWildcardHandler);
      return () => {
        const i = wildcards.indexOf(handler as PlatformWildcardHandler);
        if (i >= 0) wildcards.splice(i, 1);
      };
    }
    const list = specific.get(name as PlatformEventName) ?? [];
    list.push(handler as PlatformEventHandler<PlatformEventName>);
    specific.set(name as PlatformEventName, list);
    return () => off(name, handler as PlatformEventHandler<K>);
  }

  function off<K extends PlatformEventName>(
    name: K | "*",
    handler: PlatformEventHandler<K> | PlatformWildcardHandler,
  ): void {
    if (name === "*") {
      const i = wildcards.indexOf(handler as PlatformWildcardHandler);
      if (i >= 0) wildcards.splice(i, 1);
      return;
    }
    const list = specific.get(name as PlatformEventName);
    if (!list) return;
    const i = list.indexOf(handler as PlatformEventHandler<PlatformEventName>);
    if (i >= 0) list.splice(i, 1);
  }

  function emit<K extends PlatformEventName>(name: K, payload: PlatformEventMap[K]): void {
    const list = specific.get(name);
    if (list) {
      for (const h of list) safe(() => h(payload));
    }
    for (const h of wildcards) safe(() => h(name, payload));
  }

  return { on, off, emit };
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[platform.events] handler threw:", e);
  }
}
